import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [site, setSite] = useState(null);
  const [roles, setRoles] = useState({});

  const refreshRoles = useCallback(async () => {
    try {
      const { data } = await api.get("/roles");
      setRoles(Object.fromEntries(data.map((r) => [r.id, r])));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { if (user) refreshRoles(); }, [user, refreshRoles]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const refreshSite = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/public");
      setSite(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 401 && localStorage.getItem(TOKEN_KEY)) {
          localStorage.removeItem(TOKEN_KEY);
          setUser(null);
        } else if (status === 403 && typeof detail === "string" && detail.includes("banlan")) {
          localStorage.removeItem(TOKEN_KEY);
          setUser(null);
          toast.error(detail);
        }
        return Promise.reject(err);
      }
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    refreshSite();
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setUser(null); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, [refreshSite]);

  const login = (data) => {
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, site, refreshSite, roles, refreshRoles }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
